import { useState, useRef } from "react";

import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import TextField, { TextFieldVariants } from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";

type AutocompleteButtonProps = {
  onSelected: (selection: string, event: any) => void;
  options: string[];
  variant?: TextFieldVariants;
  children: any;
  placeholderText?: string;
  onInput?: (state: boolean) => void;
  disabled?: boolean;
  onTextInput?: (text: string) => void;
  sx: any;
  renderOption?: any;
};

export function AutocompleteButton({
  onSelected,
  options,
  variant,
  children,
  placeholderText,
  onInput,
  disabled,
  onTextInput,
  sx,
  renderOption,
}: AutocompleteButtonProps) {
  const [input, setInput] = useState<boolean>(false);
  const ref = useRef<any>();

  const changeInput = (state: boolean) => {
    setInput(state);
    if (onInput) {
      onInput(state);
    }
  };

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onSelected((e.target as any).value, e);
      changeInput(false);
    } else if (e.key === "Escape") {
      changeInput(false);
    } else if (e.key === "Tab") {
      ref.current = e;
    }
  };

  const handleChange = (e: React.SyntheticEvent, val: string | null) => {
    if (val !== null) {
      if (e.type === "click") {
        onSelected(val, e);
      } else if (
        e.type === "blur" &&
        ref.current &&
        ref.current.key === "Tab"
      ) {
        onSelected(val, ref.current);
      }
      changeInput(false);
    }
  };

  const handleTextInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onTextInput) {
      onTextInput(e.target.value);
    }
  };

  return (
    <>
      {!input && (
        <Box>
          <IconButton
            size="small"
            onClick={() => {
              changeInput(true);
            }}
            disabled={disabled}
          >
            {children}
          </IconButton>
        </Box>
      )}
      {input && (
        <Box sx={{ maxWidth: "150px", width: "100%" }}>
          <Autocomplete
            freeSolo
            autoHighlight
            autoSelect
            clearOnEscape
            onChange={handleChange}
            options={options}
            size="small"
            sx={{ pl: 1 }}
            componentsProps={{ popper: { style: { width: "fit-content" } } }}
            renderOption={renderOption}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{ fontSize: 9, width: "100%", ...(sx ? sx : {}) }}
                size="small"
                variant={variant}
                autoFocus
                placeholder={placeholderText}
                onChange={handleTextInput}
                onBlur={(e) => {
                  // give a chance to downstream event to handle
                  // like allow the label to be removed from StoryLabels when working in
                  // minimized form
                  setTimeout(() => changeInput(false), 120);
                }}
                onKeyDown={handleKey}
              />
            )}
          />
        </Box>
      )}
    </>
  );
}
